import { useMemo, type ElementType } from 'react';
import { seesOthersLogs } from '../utils/logDetailModel';
import { seesTaskSplits } from '../utils/documentReviewModel';
import { useTranslation } from 'react-i18next';
import {
  Home, FileText, MessageSquare, ListTodo, FolderKanban, Milestone, Video, Mic,
  Users, Contact, Briefcase, Folder, PackageCheck, Target, Receipt, Package,
  UserCheck, Clock, ListChecks, DollarSign, ReceiptText, Wallet, BarChart3,
  UserCog, Shield, KeyRound, Bug, Cable, Wrench, Mail, ShieldBan, Key, HardDrive, ShieldCheck, Stethoscope,
  FileSignature,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { activeNavPath, buildNavModel, isNavMatch, type NavGroupId, type NavRole } from './navModel';

export interface NavItem {
  id: string;
  to: string;
  label: string;
  icon: ElementType;
  /** Always true: hidden items are not in the model. Kept for older callers. */
  show: boolean;
}

export interface NavGroup {
  id: NavGroupId;
  /** Heading; empty for the first block ("היום שלי"), which has none. */
  label: string;
  pinned: boolean;
  items: NavItem[];
}

/** One unique icon per destination (PRD §5). */
const ICONS: Record<string, ElementType> = {
  today: Home,
  tickets: FileText,
  conversations: MessageSquare,
  tasks: ListTodo,
  projects: FolderKanban,
  milestones: Milestone,
  meetings: Video,
  voice: Mic,
  organizations: Users,
  contacts: Contact,
  engagements: Briefcase,
  documents: Folder,
  deliveries: PackageCheck,
  leads: Target,
  quotes: Receipt,
  documentsToSign: FileSignature, // FIX-D337
  taskSplits: ListChecks, // FIX-D337 stage ו
  products: Package,
  attendance: UserCheck,
  workLogs: Clock,
  logDetail: ListChecks,
  billing: DollarSign,
  obligations: ReceiptText,
  cashFlow: Wallet,
  reports: BarChart3,
  employees: UserCog,
  permissions: Shield,
  portalAccess: KeyRound,
  bugReports: Bug,
  integrations: Cable,
  systemSettings: Wrench,
  emailTemplates: Mail,
  blocklist: ShieldBan,
  api: Key,
  backups: HardDrive,
  auditTrail: ShieldCheck,
  diagnostics: Stethoscope,
};

/**
 * The app's navigation (PRD §5): shared by the desktop sidebar, the mobile
 * "עוד" sheet and the command palette, so all three expose exactly the same
 * destinations. Only the screens this person may open are in it
 * (`usePermissions`); a group left empty is not shown.
 */
export function useNavGroups(): NavGroup[] {
  const { user, organization } = useAuthStore();
  const { t } = useTranslation();
  const { canScreen, capabilities, openedByOverride, seesAllEmployees } = usePermissions();
  const role = user?.role as NavRole;
  const orgId = organization?.id;
  // P-32b (D-186): "לוח בקרה" is a tab of "היום שלי", no menu item of its own.
  // D-319 sec.9: "פירוט לוגים" only for whoever the server lets see other people's logs (the capability, not only the role).
  // D-335: or his own "view" / "edit" override on the page.
  const sees = seesOthersLogs(capabilities) || openedByOverride('logDetail') || seesAllEmployees('logDetail');
  // FIX-D337-2 §6: "חלוקות לאישור" only for the OTE manager / a team lead (or opened by an override).
  const splits = seesTaskSplits(capabilities, openedByOverride('taskSplits'));
  return useMemo(() => buildNavModel(role, orgId, canScreen).map((g) => ({
    id: g.id,
    pinned: g.pinned,
    // C-08 (D-10): the employee's day-ordered groups carry their own label keys.
    label: g.id === 'today' ? '' : t(`shell.navGroup.${g.labelKey ?? g.id}`),
    items: g.items.filter((i) => (i.id !== 'logDetail' || sees) && (i.id !== 'taskSplits' || splits)).map((i) => ({
      id: i.id,
      to: i.to,
      label: t(`shell.nav.${i.id}`),
      icon: ICONS[i.id] ?? FileText,
      show: true,
    })),
  })), [role, orgId, canScreen, t, sees, splits]);
}

/** The one active destination for the current path (most specific match). */
export function useActiveNavPath(groups: NavGroup[], pathname: string): string | null {
  return useMemo(
    () => activeNavPath(pathname, groups.flatMap((g) => g.items.map((i) => i.to))),
    [groups, pathname],
  );
}

/** Segment-aware match (kept for older callers; prefer `useActiveNavPath`). */
export function isNavActive(pathname: string, path: string): boolean {
  return isNavMatch(pathname, path);
}
